import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { checkUnreadNotifications } from "../redux/actions/notificationActions";

const useNotificationCheck = () => {
  const [showModal, setShowModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(0);

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { notifications } = useSelector((state) => state.getListNotifications);

  // Function to check for unread notifications
  const checkForUnreadNotifications = useCallback(() => {
    if (!userInfo || isChecking) return;

    const currentTime = Date.now();

    // Only check for new notifications every 2 minutes
    if (currentTime - lastCheckTime < 2 * 60 * 1000 && notifications && notifications.length > 0) {
      // Use existing notifications from Redux store
      const unreadCount = notifications.filter(n => !n.is_vu).length;
      setUnreadCount(unreadCount);

      if (unreadCount > 0) {
        setShowModal(true);
      }

      return;
    }

    // Time to check for new notifications
    setIsChecking(true);
    setLastCheckTime(currentTime);

    // Dispatch action to get notifications
    dispatch(checkUnreadNotifications());
  }, [userInfo, isChecking, lastCheckTime, notifications, dispatch]);

  // Effect to update unread count when notifications change
  useEffect(() => {
    if (notifications && notifications.length > 0) {
      const count = notifications.filter(n => !n.is_vu).length;
      setUnreadCount(count);

      if (count > 0) {
        setShowModal(true);
      }

      setIsChecking(false);
    }
  }, [notifications]);

  // Close the modal
  const closeModal = () => {
    setShowModal(false);
  };

  // Check for notifications on mount and every 2 minutes
  useEffect(() => {
    // Initial check
    checkForUnreadNotifications();

    // Set up interval for checking every 2 minutes
    const intervalId = setInterval(() => {
      checkForUnreadNotifications();
    }, 2 * 60 * 1000); // 2 minutes in milliseconds

    // Clean up interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [checkForUnreadNotifications]);

  return {
    showModal,
    unreadCount,
    closeModal,
    checkForUnreadNotifications,
  };
};

export default useNotificationCheck;
